import { ChangeEvent, memo, useCallback, useEffect, useRef, useState } from "react";
import { Security } from "../../../../proto/generated/security";
import { ClickAwayListener, Typography } from "@mui/material";
import ExtraSmallTextField from "../../../common/ExtraSmallTextField";
import { useSelector } from "react-redux";
import { selectOrderType } from "../../../../selectors/strategyBuilder";
import { Broker_OrderType, TransactionType } from "../../../../proto/generated/order";
import AnalyticsManager from "../../../../analytics/AnalyticsManager";
import { roundToNearestMultiple } from "../../../../util/tradepanel";
import OrderBookSelectionDialog from "../../../quickTradeWindow/OrderBookSelectionDialog";

export type PriceCellProps = {
    security: Security,
    transactionType: TransactionType,
    price: number,
    disabled?: boolean,
    onChange?: (price: number) => void
}

const PriceCell: React.FC<PriceCellProps> = ({ price, security, transactionType, disabled, onChange }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const orderType = useSelector(selectOrderType);
    const [priceValue, setPriceValue] = useState<number | string>(price / (security.priceMultiplier ?? 100));

    const onInputPriceChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        AnalyticsManager.logEvent('Price Changed From Input Field', { source: 'Trade' })
        setPriceValue(event.currentTarget.value === '' ? '' : Number(event.currentTarget.value));
    }, [])

    useEffect(() => {
        price && setPriceValue(price / (security.priceMultiplier ?? 100));
    }, [price])

    const onInputBlur = useCallback((e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement, Element>) => {
        let newPrice = Math.round(roundToNearestMultiple(Number(e.target.value), transactionType, false, security.tickSize / (security.priceMultiplier ?? 100)) * (security.priceMultiplier ?? 100))
        if(price === newPrice) return
        if (Number(priceValue) < 0) {
            newPrice = 0;
        }
        setPriceValue(newPrice / (security.priceMultiplier ?? 100))
        onChange?.(parseInt(`${newPrice}`));
    }, [security, priceValue, onChange]);

    const handlePriceKeyDown = useCallback((e: any) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.target.blur()
            // handleClosePopover()
        }
    }, [])

    const onFocus = useCallback((e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement, Element>) => {
        setAnchorEl(e.currentTarget)
    }, [])



    return orderType === Broker_OrderType.MARKET_ORDER ?
        <Typography variant="caption">Market</Typography>
        :
        <ClickAwayListener
            mouseEvent="onMouseDown"
            touchEvent="onTouchStart"
            onClickAway={() => {
                setAnchorEl(null);
            }}
        >
            <div style={{ display: 'inline-flex' }}>
                <ExtraSmallTextField
                    textFieldProps={{
                        value: priceValue,
                        onChange: onInputPriceChange,
                        onKeyDown: handlePriceKeyDown,
                        onBlur: onInputBlur,
                        onFocus: onFocus,
                        inputProps: { sx: { maxWidth: 54 } },
                        disabled,
                        type: 'number',
                    }}
                    hideEndIcon
                />

                <OrderBookSelectionDialog
                    anchorEl={anchorEl}
                    security={security}
                    handleOrderBookPriceChange={(price) => {
                        setPriceValue(price);
                        onChange?.(Math.round(price * (security.priceMultiplier ?? 100)));
                        setAnchorEl(null);
                    }}
                />
            </div>
        </ClickAwayListener>
}

export default memo(PriceCell);